import { dashboardApplicationsService } from '@/api/dashboardApplication';
import { Icon, Spinner, Typography } from '@/components/common';
import Icons from '@/const/icons';
import React from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';

interface NavbarSearchMenuProps {
	searchData: any | undefined;
	search?: string;
	isFetching?: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type menuOption = 'client' | 'case' | undefined;
interface User {
	user_id: string;
	full_name: string;
	email: string;
	cases: Case[];
}
interface Case {
	case_id: string;
	case_name: string;
}
const NavbarSearchMenu: React.FC<NavbarSearchMenuProps> = ({
	search,
	isFetching,
	searchData,
}) => {
	const router = useRouter();
	const [users, setUsers] = React.useState<User[]>([]);
	const [cases, setCases] = React.useState<Case[]>([]);
	const [selectedOption, setSelectedOption] = React.useState<menuOption>();
	const [selectedUser, setSelectedUser] = React.useState<User | undefined>();
	const [selectedCase, setSelectedCase] = React.useState<Case | undefined>();
	const [loading, setLoading] = React.useState<boolean>(false);
	React.useEffect(() => {
		const cases = searchData?.cases;
		const users = searchData?.users;
		if (cases) {
			setCases(cases);
		} else {
			setCases([]);
		}

		if (users) {
			setUsers(users);
		} else {
			setUsers([]);
		}
	}, [searchData]);

	const highlightText = (text: string) => {
		if (!text) return '';
		if (!search) {
			return <span style={{ opacity: 0.6 }}>{text}</span>;
		}

		const parts = text.split(new RegExp(`(${search})`, 'gi'));
		return parts.map((part, index) => {
			const isMatch = part.toLowerCase() === search.toLowerCase();
			return (
				<span key={index} style={{ opacity: isMatch ? 1 : 0.6 }}>
					{part}
				</span>
			);
		});
	};
	const isHighlighted = (text: string) => {
		if (!search || !text) return false; // If no search term, no highlight
		return text.toLowerCase().includes(search.toLowerCase());
	};

	const handleSelectUser = async (userName: string) => {
		try {
			setLoading(true);
			const response = await dashboardApplicationsService.getClientAndCasesList(
				userName
			);
			if (response.data) {
				const cases = response.data.cases;
				const user = response.data.users[0];
				setSelectedUser({
					cases: cases,
					full_name: user.full_name,
					email: user.email,
					user_id: user.user_id,
				});
				setSelectedOption('client');
				setSelectedCase(undefined);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};
	const handleSelectCase = async (caseName: string) => {
		try {
			setLoading(true);
			const response = await dashboardApplicationsService.getClientAndCasesList(
				caseName
			);
			if (response.data) {
				setSelectedCase(response.data.cases[0]);
				setSelectedUser(undefined);
				setSelectedOption('case');
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};
	return (
		<div>
			{isFetching || loading ? (
				<div className="flex justify-center items-center py-6">
					<Spinner type="loadingPage" />
				</div>
			) : (
				<>
					{selectedOption && (
						<div className="pb-4 border-b border-gray-100 ">
							<div
								className="flex gap-3 w-fit cursor-pointer items-center"
								onClick={() => setSelectedOption(undefined)}
							>
								<Icon className="!h-4 !w-4" src={Icons.arrowLeft} />
								<Typography type="semibold-14">Back</Typography>
							</div>
						</div>
					)}
					{selectedOption === 'client' && (
						<div className="mt-8">
							{selectedUser && selectedUser.cases.length > 0 && (
								<>
									<div className="flex gap-3 w-fit  items-center mb-4">
										<Icon
											className="!h-4 !w-4 !text-primary"
											src={Icons.suitcaseThick}
										/>
										<Typography type="semibold-14">Cases</Typography>
									</div>
									<ul className="flex flex-col gap-1 list-none max-h-[130px] overflow-y-auto scroll-custom mb-8">
										{selectedUser.cases.map((item, index) => (
											<Link
												href={`/lawyerapp/cases/${item.case_id}?tab=case-clients`}
												key={`${item.case_id}- ${index}`}
												className="flex items-center gap-2 py-1 px-2 hover:bg-gray-5 cursor-default opacity-60"
											>
												<span>•</span>
												<Typography type="regular-14">
													{item.case_name}
												</Typography>
											</Link>
										))}
									</ul>
								</>
							)}
							<div
								className="flex gap-3 w-fit  cursor-pointer items-center"
								onClick={() => {
									if (selectedUser) {
										router.push(
											`/lawyerapp/client-profile/${selectedUser.user_id}`
										);
									}
								}}
							>
								<Icon className="!h-4 !w-4 !text-primary" src={Icons.profile} />
								<Typography type="semibold-14">Go to Profile</Typography>
								<Icon className="!h-4 !w-4 !rotate-180" src={Icons.arrowLeft} />
							</div>
						</div>
					)}
					{selectedOption === 'case' && (
						<div className="mt-8">
							<div
								className="flex gap-3 w-fit  cursor-pointer items-center"
								onClick={() => {
									if (selectedCase) {
										router.push(
											`/lawyerapp/cases/${selectedCase.case_id}/investigation`
										);
									}
								}}
							>
								<Icon
									className="!h-4 !w-4 !text-primary"
									src={Icons.suitcaseThick}
								/>
								<Typography type="semibold-14">Go to Case Details</Typography>
								<Icon className="!h-4 !w-4 !rotate-180" src={Icons.arrowLeft} />
							</div>
						</div>
					)}
					{!selectedOption && (
						<>
							{users.length > 0 && (
								<div className="mb-4">
									<Typography type="semibold-12" className="mb-1">
										Clients
									</Typography>
									<ul className="flex flex-col gap-1 list-none max-h-[110px] overflow-y-auto scroll-custom">
										{users.map((client, index) => (
											<li
												key={`${client.user_id}- ${index}`}
												className="flex items-center gap-2 py-1 px-2 hover:bg-gray-5 cursor-default"
												onClick={() => {
													handleSelectUser(client.full_name);
												}}
											>
												<span
													style={{
														opacity: isHighlighted(client.full_name) ? 1 : 0.6, // Dot opacity changes based on search result in case title
													}}
												>
													•
												</span>
												<Typography type="regular-14">
													{highlightText(client.full_name)}
												</Typography>
											</li>
										))}
									</ul>
								</div>
							)}

							{cases.length > 0 && (
								<div className="mb-4">
									<Typography type="semibold-12" className="mb-1">
										Cases
									</Typography>

									<ul className="flex flex-col gap-1 list-none max-h-[110px] overflow-y-auto scroll-custom">
										{cases.map((item, index) => (
											<li
												key={`${item.case_id}- ${index}`}
												className="flex items-center gap-2 py-1 px-2 hover:bg-gray-5 cursor-default"
												onClick={() => handleSelectCase(item.case_name)}
											>
												<span
													style={{
														opacity: isHighlighted(item.case_name) ? 1 : 0.6,
													}}
												>
													•
												</span>
												<Typography type="regular-14">
													{highlightText(item.case_name)}
												</Typography>
											</li>
										))}
									</ul>
								</div>
							)}
							{search && cases.length === 0 && users.length === 0 && (
								<div className="bg-gray-20 h-[158px] border-dashed border border-[#0d061614] rounded-2xl flex justify-center items-center">
									<Typography type="regular-16" className="opacity-60">
										<p className="text-center">No matches found.</p>
										<p className="text-center">
											Please refine your search and try again.
										</p>
									</Typography>
								</div>
							)}
						</>
					)}
				</>
			)}
		</div>
	);
};

export default NavbarSearchMenu;
